import { Alert, Card, Form, Table, Button } from "react-bootstrap";
import { DatagridHelper } from "../../datagrid/DatagridHelper";
import { DataType, MessageType, ResponseStatus } from "../../utils/types";
import Loading from "../../utils/Loding";
import { CampaignsResponseDto } from "../../utils/types";
import { CampaignsResponseDtoCampaign } from "../../utils/types";
import { CampaignsResponseDtoUnconfiguredCampaign } from "../../utils/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faCircleExclamation, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { PagesHelper } from "../../common/PagesHelper";
import { LookupApp } from "../Lookup/types";
import { useState, useEffect } from "react";

type SortColumn = 'status' | 'source' | 'campaign' | 'countries' | 'action' | 'clicks1d' | 'fillRate1d' | 'conversions1d' | 'clicks7d' | 'fillRate7d' | 'conversions7d';
type SortDirection = 'asc' | 'desc';

interface KnownEvent {
  grossCPA?: number;
  eventDesc?: string;
  eventSourceType?: string;
  eventType?: string;
  eventTypeCode?: string;
  isDefault?: boolean;
  maxPayable?: number;
  dailyCap?: number;
}

const formatKnownEvent = (event: KnownEvent): string => {
  return (event.eventDesc || event.eventType || `${event.eventTypeCode || 'Event'} ($${event.grossCPA || 0})`).toUpperCase();
};

const formatKnownEvents = (knownEvents: string | KnownEvent | KnownEvent[] | undefined): string => {
  if (!knownEvents) return '-';
  if (typeof knownEvents === 'string') return knownEvents.toUpperCase();
  if (Array.isArray(knownEvents)) return knownEvents.map(formatKnownEvent).join(', ');
  if (typeof knownEvents === 'object') return formatKnownEvent(knownEvents);
  return 'Event';
};

interface LookupCampaign {
  campaignID?: string;
  campaign_id?: string;
  dispCampaignID?: string;
  plutoCampaignID?: number;
  source?: string;
  supplySource?: string;
  status?: string;
  countries?: string | string[];
  knownEvents?: string | KnownEvent | KnownEvent[];
  deviceIDRequired?: boolean;
  incent?: boolean;
  buyLink?: string;
  stats?: {
    todayBuys?: number;
    todayRTBFillRate?: number;
    todayFillRate?: number;
    todayInstalls?: number;
    weekBuys?: number;
    weekRTBFillRate?: number;
    weekFillRate?: number;
    weekInstalls?: number;
  };
  campaignMatch?: boolean;
}

interface CampaignsTableProps {
  // For clink management variant
  campaignsResponseDto?: CampaignsResponseDto;
  setCampaignsResponseDto?: (campaignsResponseDto:CampaignsResponseDto) => void;
  unconfigured?: boolean;
  headerTitle?: string | React.ReactNode;
  setCampaignsAreDirty?: (campaignsAreDirty: boolean) => void;
  
  // For display variant (Lookup)
  variant?: 'clink-management' | 'display';
  campaigns?: LookupCampaign[];
  matchingOnly?: boolean;
  formatNumber?: (num: number | undefined) => string;
  formatPercentage?: (num: number | undefined) => string;
  formatCurrency?: (num: number | undefined) => string;
  onCreateClink?: (app: LookupApp, campaignId?: string, demandSourceId?: string) => void;
  onShowStats?: (app: LookupApp, campaign?: LookupCampaign) => void;
  app?: LookupApp;
  showCard?: boolean;
}

const cardHeaderStyle = {
  fontWeight: "bold",
  fontSize: "1.2rem"
}

function CampaignsTable(props:CampaignsTableProps) {
  const { 
    campaignsResponseDto, 
    setCampaignsResponseDto, 
    unconfigured, 
    headerTitle, 
    setCampaignsAreDirty,
    variant = 'clink-management',
    campaigns,
    matchingOnly = false,
    formatNumber = (num) => num?.toLocaleString() || '0',
    formatPercentage = (num) => num ? `${(num * 100).toFixed(2)}%` : '0%',
    // formatCurrency = (num) => num ? `$${num.toFixed(2)}` : '$0.00', // Currently unused
    onCreateClink,
    onShowStats,
    app,
    showCard = true
  } = props;

  // Sorting state
  const [sortColumn, setSortColumn] = useState<SortColumn>('clicks7d');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Force re-render when demand sources are loaded
  const [, forceUpdate] = useState({});

  // Handle sorting
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc'); // Default to descending for new columns
    }
  };

  // Sort campaigns
  const sortCampaigns = (campaignsToSort: LookupCampaign[]) => {
    return [...campaignsToSort].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortColumn) {
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        case 'source':
          aValue = a.source || '';
          bValue = b.source || '';
          break;
        case 'campaign':
          aValue = a.dispCampaignID || a.campaignID || '';
          bValue = b.dispCampaignID || b.campaignID || '';
          break;
        case 'countries':
          aValue = (typeof a.countries === 'string' 
                     ? a.countries 
                     : Array.isArray(a.countries) 
                       ? a.countries.join(', ')
                       : a.countries ? JSON.stringify(a.countries) : '') || '';
          bValue = (typeof b.countries === 'string' 
                     ? b.countries 
                     : Array.isArray(b.countries) 
                       ? b.countries.join(', ')
                       : b.countries ? JSON.stringify(b.countries) : '') || '';
          break;
        case 'action':
          aValue = formatKnownEvents(a.knownEvents);
          bValue = formatKnownEvents(b.knownEvents);
          break;
        case 'clicks1d':
          aValue = a.stats?.todayBuys || 0;
          bValue = b.stats?.todayBuys || 0;
          break;
        case 'fillRate1d':
          aValue = a.stats?.todayRTBFillRate || a.stats?.todayFillRate || 0;
          bValue = b.stats?.todayRTBFillRate || b.stats?.todayFillRate || 0;
          break;
        case 'conversions1d':
          aValue = a.stats?.todayInstalls || 0;
          bValue = b.stats?.todayInstalls || 0;
          break;
        case 'clicks7d':
          aValue = a.stats?.weekBuys || 0;
          bValue = b.stats?.weekBuys || 0;
          break;
        case 'fillRate7d':
          aValue = a.stats?.weekRTBFillRate || a.stats?.weekFillRate || 0;
          bValue = b.stats?.weekRTBFillRate || b.stats?.weekFillRate || 0;
          break;
        case 'conversions7d':
          aValue = a.stats?.weekInstalls || 0;
          bValue = b.stats?.weekInstalls || 0;
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return sortDirection === 'asc' ? comparison : -comparison;
      }

      const comparison = (aValue || 0) - (bValue || 0);
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  useEffect(() => {
    // Listen for demand sources loaded event
    const handleDemandSourcesLoaded = () => {
      forceUpdate({}); // Trigger re-render
    };

    window.addEventListener('demandSourcesLoaded', handleDemandSourcesLoaded);

    return () => {
      window.removeEventListener('demandSourcesLoaded', handleDemandSourcesLoaded);
    };
  }, []);

  // Handle display variant for Lookup
  if (variant === 'display') {
    const tableContent = (
      <div style={{ overflowX: 'auto' }}>
        <Table striped bordered hover size="sm">
          <thead>
          <tr>
            <th style={{cursor: 'pointer', whiteSpace: 'nowrap'}} onClick={() => handleSort('status')}>
              Status <span style={{opacity: sortColumn === 'status' ? 1 : 0}}>{sortDirection === 'asc' ? '▲' : '▼'}</span>
            </th>
            <th style={{cursor: 'pointer', whiteSpace: 'nowrap'}} onClick={() => handleSort('source')}>
              Demand Source <span style={{opacity: sortColumn === 'source' ? 1 : 0}}>{sortDirection === 'asc' ? '▲' : '▼'}</span>
            </th>
            <th style={{cursor: 'pointer', whiteSpace: 'nowrap'}} onClick={() => handleSort('campaign')}>
              Campaign <span style={{opacity: sortColumn === 'campaign' ? 1 : 0}}>{sortDirection === 'asc' ? '▲' : '▼'}</span>
            </th>
            <th style={{cursor: 'pointer', whiteSpace: 'nowrap'}} onClick={() => handleSort('countries')}>
              Countries <span style={{opacity: sortColumn === 'countries' ? 1 : 0}}>{sortDirection === 'asc' ? '▲' : '▼'}</span>
            </th>
            <th style={{cursor: 'pointer', whiteSpace: 'nowrap'}} onClick={() => handleSort('action')}>
              Action <span style={{opacity: sortColumn === 'action' ? 1 : 0}}>{sortDirection === 'asc' ? '▲' : '▼'}</span>
            </th>
            <th className="text-center" style={{cursor: 'pointer', whiteSpace: 'nowrap'}} onClick={() => handleSort('clicks1d')}>
              1d Clicks <span style={{opacity: sortColumn === 'clicks1d' ? 1 : 0}}>{sortDirection === 'asc' ? '▲' : '▼'}</span>
            </th>
            <th className="text-center" style={{cursor: 'pointer', whiteSpace: 'nowrap'}} onClick={() => handleSort('fillRate1d')}>
              1d Fill Rate <span style={{opacity: sortColumn === 'fillRate1d' ? 1 : 0}}>{sortDirection === 'asc' ? '▲' : '▼'}</span>
            </th>
            <th className="text-center" style={{cursor: 'pointer', whiteSpace: 'nowrap'}} onClick={() => handleSort('conversions1d')}>
              1d Conv. <span style={{opacity: sortColumn === 'conversions1d' ? 1 : 0}}>{sortDirection === 'asc' ? '▲' : '▼'}</span>
            </th>
            <th className="text-center" style={{cursor: 'pointer', whiteSpace: 'nowrap'}} onClick={() => handleSort('clicks7d')}>
              7d Clicks <span style={{opacity: sortColumn === 'clicks7d' ? 1 : 0}}>{sortDirection === 'asc' ? '▲' : '▼'}</span>
            </th>
            <th className="text-center" style={{cursor: 'pointer', whiteSpace: 'nowrap'}} onClick={() => handleSort('fillRate7d')}>
              7d Fill Rate <span style={{opacity: sortColumn === 'fillRate7d' ? 1 : 0}}>{sortDirection === 'asc' ? '▲' : '▼'}</span>
            </th>
            <th className="text-center" style={{cursor: 'pointer', whiteSpace: 'nowrap'}} onClick={() => handleSort('conversions7d')}>
              7d Conv. <span style={{opacity: sortColumn === 'conversions7d' ? 1 : 0}}>{sortDirection === 'asc' ? '▲' : '▼'}</span>
            </th>
            <th className="text-center">DevID</th>
            <th className="text-center">Incent</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {!campaigns || campaigns.length === 0 ? (
            <tr>
              <td colSpan={14} className="text-center">No campaigns</td>
            </tr>
          ) : (
            // Filter and sort campaigns based on matchingOnly prop and sorting state
            sortCampaigns(matchingOnly 
              ? campaigns.filter(c => c.campaignMatch === true) 
              : campaigns
            ).map((campaign: LookupCampaign, idx: number) => (
              <tr key={idx}>
                <td>{campaign.status}</td>
                <td><span title={campaign.source || campaign.supplySource}>{PagesHelper.getDemandSourceLabel(campaign.source || campaign.supplySource, false)}</span></td>
                <td>
                  {campaign.plutoCampaignID ? (
                    <span title={`view ${campaign.dispCampaignID || campaign.campaignID || campaign.campaign_id} in Pluto`}>
                      <a 
                        target="_blank" 
                        rel="noopener noreferrer"
                        href={`${process.env.REACT_APP_PLUTO_BASE_URL}/campaigns/view/${campaign.plutoCampaignID}`}
                      >
                        {campaign.dispCampaignID || campaign.campaignID || campaign.campaign_id}
                        <i className="fa fa-pencil-square-o" style={{marginLeft: '5px'}}></i>
                      </a>
                    </span>
                  ) : (
                    <span>{campaign.campaignID || campaign.campaign_id || campaign.dispCampaignID}</span>
                  )}
                </td>
                <td>
                  {campaign.countries 
                    ? (typeof campaign.countries === 'string' 
                       ? campaign.countries.toUpperCase()
                       : Array.isArray(campaign.countries) 
                         ? campaign.countries.join(', ').toUpperCase()
                         : JSON.stringify(campaign.countries))
                    : '-'}
                </td>
                <td>{formatKnownEvents(campaign.knownEvents)}</td>
                <td className="text-right">{formatNumber(campaign.stats?.todayBuys || 0)}</td>
                <td className="text-right">{formatPercentage(campaign.stats?.todayRTBFillRate || campaign.stats?.todayFillRate || 0)}</td>
                <td className="text-right">{formatNumber(campaign.stats?.todayInstalls || 0)}</td>
                <td className="text-right">{formatNumber(campaign.stats?.weekBuys || 0)}</td>
                <td className="text-right">{formatPercentage(campaign.stats?.weekRTBFillRate || campaign.stats?.weekFillRate || 0)}</td>
                <td className="text-right">{formatNumber(campaign.stats?.weekInstalls || 0)}</td>
                <td className="text-center">
                  {campaign.deviceIDRequired ? (
                    <i className="fa fa-gear" title="Device ID required"></i>
                  ) : (
                    <i className="fa fa-gear" style={{color: '#ccc'}} title="Device ID not required"></i>
                  )}
                </td>
                <td className="text-center">
                  {campaign.incent ? (
                    <i className="fa fa-star" title="Incentivized traffic allowed"></i>
                  ) : (
                    <i className="fa fa-star-o" style={{color: '#ccc'}} title="NO incentivized traffic"></i>
                  )}
                </td>
                <td>
                  <div className="d-flex gap-1">
                    {onShowStats && app && (
                      <Button size="sm" variant="info" onClick={() => onShowStats(app, campaign)} title="Campaign Stats">
                        <i className="fa fa-bar-chart"></i>
                      </Button>
                    )}
                    {onCreateClink && app && (
                      <Button size="sm" variant="success" onClick={() => onCreateClink(
                        app,
                        campaign.campaignID,
                        campaign.supplySource || campaign.source
                      )} title="Create cLink">
                        <i className="fa fa-share-alt"></i>
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
        </Table>
      </div>
    );

    if (!showCard) {
      return tableContent;
    }

    return (
      <Card>
        <Card.Header style={cardHeaderStyle}>{headerTitle || "Campaigns"}</Card.Header>
        {tableContent}
      </Card>
    );
  }

  // Original clink management variant logic
  if (campaignsResponseDto && campaignsResponseDto.status === ResponseStatus.LOADING) return <Loading />;
  if (!campaignsResponseDto || campaignsResponseDto.status === ResponseStatus.ERROR) {
    return (
      <Alert variant={MessageType.DANGER}>
        <span>{campaignsResponseDto && campaignsResponseDto.error ? campaignsResponseDto.error : "Error loading campaigns"}</span>
      </Alert>
    );
  }

  const handleSelectCampaign = (index:number, isUnconfigured:boolean ,checked:boolean) => {
    const unconfiguredCampaigns:CampaignsResponseDtoUnconfiguredCampaign[] = isUnconfigured ? campaignsResponseDto.unconfiguredCampaigns.map((campaign, cIndex) => index === cIndex ? { ...campaign, added: checked } : campaign) : campaignsResponseDto.unconfiguredCampaigns;
    const campaigns:CampaignsResponseDtoCampaign[] = !isUnconfigured ? campaignsResponseDto.campaigns.map((campaign, cIndex) => index === cIndex ? { ...campaign, added: checked } : campaign) : campaignsResponseDto.campaigns;

    setCampaignsResponseDto!({ ...campaignsResponseDto, campaigns, unconfiguredCampaigns });
    if (setCampaignsAreDirty) {
      setCampaignsAreDirty(true);
    }
  }

  return (
    <Card>
      <Card.Header style={cardHeaderStyle}>{headerTitle ? headerTitle : "Campaigns"}</Card.Header>
      <div style={{ overflowX: 'auto' }}>
        <Table style={{ fontSize: 11 }} striped bordered hover>
        {
          (unconfigured) ?
          <>
            <thead>
              <tr>
                <th title="The demand source providing this campaign">Demand Source</th>
                <th title="Unique identifier for this campaign">Campaign ID</th>
                <th title="Select to include this campaign in the cLink">Include Campaign</th>
              </tr>
            </thead>
            <tbody>
              {
                campaignsResponseDto.unconfiguredCampaigns.map((campaign, index:number) => {
                  return (
                    <tr key={index} className="{{campaign.source}}">
                      <td>
                        <span data-tooltip="{{campaign.source}}">
                          {PagesHelper.getDemandSourceLabel(campaign.source)}
                        </span>
                      </td>
                      <td>
                        <span data-tooltip="{{campaign.campaign_id}}">
                          {campaign.campaign_id}
                        </span>
                      </td>
                      <td style={{ textAlign: "center" }}> 
                        <Form.Check
                          inline
                          type="checkbox"
                          style={{ marginTop: "-0.1rem"}}
                          checked={campaign.added}
                          disabled={campaignsResponseDto.unconfiguredCampaigns.find((c) => c.added && c.incent !== campaign.incent) !== undefined}
                          onChange={(e) => handleSelectCampaign(index, true, e.target.checked)}
                        />
                      </td>
                    </tr>
                  );
                })
              }
            </tbody>
          </>
          :
          <>
            <thead>
              <tr>
                <th title="Campaign status: ACTIVE, PAUSED, or ENDED">Status</th>
                <th title="The demand source providing this campaign">Demand Source</th>
                <th title="Unique identifier for this campaign">CampaignID</th>
                <th title="Geographic regions where this campaign can serve">Countries</th>
                <th title="Daily cap limit for this campaign">Limit</th>
                <th title="Event amount for this campaign">Event</th>
                <th title="Supported device platforms">Platforms</th>
                <th title="Minimum required operating system version">OS Version</th>
                <th title="Date when this campaign expires">End Date</th>
                <th title="Whether device advertising ID is required for attribution (Pluto tag: Device ID Required)">DeviceID Required</th>
                <th title="Whether incentivized traffic is permitted (Pluto tag: Allow Incent)">Allow Incent</th>
                <th style={{ textAlign: "center" }} title="Select to include this campaign in the cLink">Include Campaign</th>
              </tr>
            </thead>
            <tbody>
              {
                campaignsResponseDto.campaigns.map((campaign, index:number) => {
                  return (
                    <tr key={index} className="{{campaign.status}}">
                      <td>
                        <span data-tooltip="{{campaign.status}}">
                          <div title={campaign.status} style={{ display: "inline-block", marginRight: "0.3rem" }}>
                            {(() => {
                              if (campaign.status === 'ACTIVE') {
                                return <FontAwesomeIcon icon={faCircleCheck} style={{color: "#5cb85c"}}/>
                              } else if (campaign.status === 'PAUSED') {
                                return <FontAwesomeIcon icon={faCircleExclamation} />;
                              } else {
                                return <FontAwesomeIcon icon={faTriangleExclamation} style={{color: "#ed9c28"}} />;
                              }
                            })()}
                          </div>
                          {campaign.status}
                        </span>
                      </td>
                      <td>
                        <span data-tooltip="{{campaign.supplySource}}">
                          {PagesHelper.getDemandSourceLabel(campaign.supplySource)}
                        </span>
                      </td>
                      <td>
                        <span data-tooltip="{{campaign.campaignID}}">
                          {campaign.campaignID}
                        </span>
                      </td>
                      <td>
                        <span data-tooltip="{{campaign.countries}}">
                          {campaign.countries}
                        </span>
                      </td>
                      <td>
                        <span data-tooltip="{{campaign.limit}}">
                          {campaign.limit !== "None"
                            ? campaign.limit
                            : (campaign.knownEvents?.[0]?.dailyCap
                                ? campaign.knownEvents[0].dailyCap.toLocaleString()
                                : "None")}
                        </span>
                      </td>
                      <td>
                        <span data-tooltip="{{campaign.payout}}">
                          {DatagridHelper.formatValue(DataType.MONEY, Number(campaign.payout))}
                        </span>
                      </td>
                      <td>
                        <span data-tooltip="{{campaign.platforms}}">
                          {campaign.platforms}
                        </span>
                      </td>
                      <td>
                        <span data-tooltip="{{campaign.osVersion}}">
                          {campaign.osVersion}
                        </span>
                      </td>
                      <td>
                        <span data-tooltip="{{campaign.enddate}}">
                          {campaign.enddate !== "n/a" ? campaign.enddate : "None"}
                        </span>
                      </td>
                      <td>
                        <span data-tooltip="{{campaign.deviceIDRequired}}">
                          {campaign.deviceIDRequired.toString()}
                        </span>
                      </td>
                      <td>
                        <span data-tooltip="{{campaign.incent}}">
                          {campaign.incent.toString()}
                        </span>
                      </td>
                      <td style={{ textAlign: "center" }}> 
                        <Form.Check
                          inline
                          type="checkbox"
                          style={{ marginTop: "-0.1rem"}}
                          checked={campaign.added}
                          disabled={campaignsResponseDto.campaigns.find((c) => c.added && c.incent !== campaign.incent) !== undefined}
                          onChange={(e:any) => handleSelectCampaign(index, false, e.target.checked)}
                        />
                      </td>
                    </tr>
                  );
                })
              }
            </tbody>
          </>
        }
        </Table>
      </div>
    </Card>
  );
}

export default CampaignsTable;